import * as R from "remeda";

import type { Write } from "../store/zustand.helpers";
import {
    clearState,
    getStateKeys,
    zustandStore,
} from "../store/zustand.helpers";
import type { Dimension } from "../types/dimensions";

export enum DimensionStatus {
    INVALID = "invalid",
    SUCCESS = "success",
    EMPTY = "empty",
    LOADING = "loading",
    ERROR = "error",
    PENDING_HYDRATION = "pending_hydration",
    READY = "ready",
}

interface DimensionStatusState {
    [key: string]: DimensionStatus;
}

interface DimensionStatusActions {
    setStatus: (dimension: string, status: DimensionStatus) => void;
    clearStatus: () => void;
    initDimensions: (dimensions: Dimension[], status?: DimensionStatus) => void;
    checkAllStatusSuccess: () => boolean;
    checkStatusSuccess: (dimensions: Dimension[]) => boolean;
    resetCurrentDimensions: () => void;
    getAllStatus: () => Record<string, DimensionStatus>;
}

const initialState: DimensionStatusState = {};

export const { store: dimensionStatusStore } = zustandStore<
    DimensionStatusState,
    DimensionStatusActions
>("Dimension Status", initialState, (set, get) => ({
    setStatus: (dimension, status) =>
        set(() => ({
            [dimension]: status,
        })),
    clearStatus: () => set((state) => clearState(state), true),
    initDimensions: (
        dimensions,
        status: DimensionStatus = DimensionStatus.EMPTY,
    ) => {
        const initial = R.pipe(
            dimensions,
            R.map((e) => [e, status] as const),
            R.fromPairs<DimensionStatus>,
        );
        set(() => initial);
    },
    checkAllStatusSuccess: () => {
        return (
            R.pipe(
                get(),
                Object.values,
                R.filter(R.isString),
                R.countBy(
                    (e) => (e as DimensionStatus) !== DimensionStatus.SUCCESS,
                ),
            ) === 0
        );
    },
    checkStatusSuccess: (dimensions) => {
        return (
            R.pipe(
                dimensions,
                R.map((e) => get()[e]),
                R.filter(R.isString),
                R.filter((e) => e === DimensionStatus.SUCCESS),
            ).length === dimensions.length
        );
    },
    resetCurrentDimensions: () => {
        const store = get() as Write<
            DimensionStatusState,
            DimensionStatusActions
        >;
        const dimensions = getStateKeys(store);
        store.initDimensions(dimensions);
    },
    getAllStatus: () => {
        const store = get() as Write<
            DimensionStatusState,
            DimensionStatusActions
        >;
        const dimensions = getStateKeys(store);
        return R.pick(get(), dimensions);
    },
}));
